import { difference } from "lodash";
export function diffNetMaps(oldNets, newNets) {
    const oldNetHashes = Object.keys(oldNets);
    const newNetHashes = Object.keys(newNets);
    const deleted = difference(oldNetHashes, newNetHashes);
    const added = difference(newNetHashes, oldNetHashes);
    const unchanged = difference(oldNetHashes, deleted, added);
    // Map of new net hashes to an array of complexity ordered old net hashes
    const mergedNetMapping = {};
    // Keep a set of merged new nets for determining actual new nets
    const mergedNets = new Set();
    // Map of old net hashes to an array of complexity ordered new net hashes
    const splitNetMapping = {};
    // Keep a set of split old nets for determining actual deleted nets
    const splitNets = new Set();
    // Use the added and deleted nets to work out which nets are split, merged or new.
    // See if the deleted nets have been merged into a newly added net
    for (const netHash of deleted) {
        const deletedNetTerminals = Object.keys(oldNets[netHash]);
        if (!deletedNetTerminals.length)
            continue;
        // Find the new net that contains all (!! not true, see below) the terminals of the deleted net.
        // Most common scenarios for merging and why this find() is ok performance wise:
        // 1. Wire is added between, creating two new nets. In this case deleted
        //    will have 2 entries and added will have 1.
        // 2. Pasting nets into the document will have multiple added and 0 deleted.
        // 3. Deleting multiple selected nets will have 0 added and multiple deleted
        const mergedNetHash = added.find((addedNetHash) => {
            const addedNet = newNets[addedNetHash];
            const addedNetTerminals = Object.keys(addedNet);
            const terminalsAdded = difference(addedNetTerminals, deletedNetTerminals);
            // TODO FLUX-5090 there is ZERO guarantee here that the nodes passing this condition (which we then
            // add to `mergedNetMapping`) are not also added to `splitNetMapping` below. The match
            // conditions are not mutually exclusive. And that's BAD. Because the same node will get
            // processed twice later in the code, given the same hash (among other problems like not
            // getting all the right pins), and we'll end up with bogus nets.
            // This affects us in real life: you can repro bugs by eg dragging a component in series
            // onto a wire (thus adding and removing terminals in the same action). It won't happen
            // every time (because it depends on arbitrary order somewhat) but frequently.
            return (
            // ((added net is not only deleted terminals) AND (some deleted terminals are in added net))
            // OR (added net has all the deleted terminals and because we're here, has no other terminals)
            (terminalsAdded.length > 0 && terminalsAdded.length < addedNetTerminals.length) ||
                // Check if net has all terminals of the deleted net and is not a subset of the deleted net
                // NOTE: This is a special case for handling a net that has an incorrect hash due to a bug in the
                // net splitting code where terminals were not removed from the net when an entire wire is deleted.
                // (!! ouch -- that sounds bad; we should fix that bug instead of adding weird code here)
                deletedNetTerminals.every((terminal) => terminal in addedNet));
        });
        if (mergedNetHash) {
            // Add both the new and old net hashes to the merged set
            mergedNets.add(netHash);
            mergedNets.add(mergedNetHash);
            // If we found a merged net, add it to the mapping.
            if (!mergedNetMapping[mergedNetHash]) {
                mergedNetMapping[mergedNetHash] = [netHash];
            }
            else {
                // If we already have the mapping, add the new netHash to the array and sort by complexity.
                const oldNetHashes = mergedNetMapping[mergedNetHash];
                oldNetHashes.push(netHash);
                // sorting on insert to avoid extra pass at the end. In most scenarios we should only have
                // nets merging so this should not be a perf issue.
                // TODO FLUX-5090 it's silly to sort here though; and it obfuscates the intent. Do it outside the loop.
                mergedNetMapping[mergedNetHash] = oldNetHashes.sort((a, b) => Object.keys(oldNets[b]).length - Object.keys(oldNets[a]).length);
            }
        }
    }
    // Go through each added net and see if it contains terminals from a deleted net
    for (const netHash of added) {
        const addedNetTerminals = Object.keys(newNets[netHash]);
        if (!addedNetTerminals.length)
            continue;
        // Find the deleted net that contains all the terminals of the added net.
        // Most common scenarios for splitting and why this find is ok performance wise:
        // 1. Wire is deleted between, creating two new nets. In this case deleted
        //    will have 1 entry and added will have 2.
        // 2. A section of an exisiting net is deleted without breaking the net. In
        //    this case deleted will have 1 entry and added will have 1 entry.
        // 3. Deleting multiple selected nets will have 0 added and multiple deleted
        //
        // TODO FLUX-5090 there is ZERO guarantee here that the nodes we find here are not already
        // in mergedNetMapping above. That's bad. See comment there.
        const splitNetHash = deleted.find((deletedNetHash) => {
            const deletedNet = oldNets[deletedNetHash];
            const deletedNetTerminals = Object.keys(deletedNet);
            const terminalsRemoved = difference(deletedNetTerminals, addedNetTerminals);
            // Return true if the deleted net has some, but not all of the terminals removed.
            // If all the terminals are removed then the net will be deleted.
            //
            // TODO I don't think that's the result of this expression! I think it's just finding
            // a deleted net that has some terminals in common with the new net (but not all), but
            // in a roundabout way.
            return terminalsRemoved.length > 0 && terminalsRemoved.length < deletedNetTerminals.length;
        });
        if (splitNetHash) {
            // Add both the new and old net hashes to the split set
            splitNets.add(splitNetHash);
            splitNets.add(netHash);
            // If we found a split net, add it to the mapping.
            if (!splitNetMapping[splitNetHash]) {
                splitNetMapping[splitNetHash] = [netHash];
            }
            else {
                // If we already have the mapping, add the new netHash to the array and sort by complexity.
                const newNetHashes = splitNetMapping[splitNetHash];
                newNetHashes.push(netHash);
                // sorting on insert to avoid extra pass at the end. In most scenarios we should only have
                // nets merging so this should not be a perf issue.
                // TODO FLUX-5090 it's silly to sort here though; and it obfuscates the intent. Do it outside the loop.
                splitNetMapping[splitNetHash] = newNetHashes.sort((a, b) => Object.keys(newNets[b]).length - Object.keys(newNets[a]).length);
            }
        }
    }
    // Work out the actual new nets
    const actualNew = added.filter((netHash) => !splitNets.has(netHash) && !mergedNets.has(netHash));
    // Work out the actual deleted nets
    const actualDeleted = deleted.filter((netHash) => !mergedNets.has(netHash) && !splitNets.has(netHash));
    return {
        unchanged,
        added: actualNew,
        deleted: actualDeleted,
        merged: mergedNetMapping,
        split: splitNetMapping,
    };
}
//# sourceMappingURL=netDiff.js.map